(() => {
  'use strict';

  const ELEMENT_NAME = 'msiv-app-promotion';
  if (customElements.get(ELEMENT_NAME)) return;

  const DAY_MS = 24 * 60 * 60 * 1000;
  const PRIVACY_TIMEOUT_MS = 5000;
  const PRIVACY_GRACE_MS = 1800;
  const IMAGE_TIMEOUT_MS = 3000;
  const PRESENTATION_TIMEOUT_MS = 3000;
  const CONFLICT_WAIT_MS = 30000;
  const CONFLICT_POLL_MS = 200;
  const MIN_VISIBLE_OPACITY = 0.5;
  const LEASE_MS = 15000;
  const STORAGE_KEY = 'msiv:app-promotion:v1';
  const COOKIE_KEY = '_msiv_app_promotion_v1';
  const DISMISS_SESSION_KEY = 'msiv:app-promotion:dismissed-session:v1';
  const DISMISS_COOKIE_KEY = '_msiv_app_promotion_dismissed_v1';
  const ALREADY_INSTALLED_STORAGE_KEY = 'msiv:app-promotion:already-installed:v1';
  const ALREADY_INSTALLED_COOKIE_KEY = '_msiv_app_promotion_already_installed_v1';
  const ALREADY_INSTALLED_RETENTION_MS = 180 * DAY_MS;
  const ALREADY_INSTALLED_COOKIE_MAX_AGE = ALREADY_INSTALLED_RETENTION_MS / 1000;
  const BUCKET_KEY = 'msiv:app-promotion:bucket:v1';
  const BUCKET_EXPIRY_KEY = 'msiv:app-promotion:bucket-expiry:v1';
  const BUCKET_COOKIE_KEY = '_msiv_app_promotion_bucket_v1';
  const LEASE_KEY = 'msiv:app-promotion:show:v1';
  const LOCK_NAME = 'msiv:app-promotion:show:v1';
  const CHANNEL_NAME = 'msiv:app-promotion:coordination:v1';
  const SEARCH_SESSION_KEY = 'msiv:app-promotion:search-session:v1';
  const SEARCH_SESSION_COOKIE_KEY = '_msiv_app_promotion_search_session_v1';
  const SEARCH_FALLBACK_KEY = 'msiv:app-promotion:search-fallback:v1';
  const IOS_STORE_URL = 'https://apps.apple.com/de/app/mein-schrank-ist-voll/id6762192244';
  const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=de.meinschrankistvoll.app';
  const RESERVE_EVENT = 'msiv:app-promotion:reserve';
  const RELEASE_EVENT = 'msiv:app-promotion:release';
  const RESERVED_ATTRIBUTE = 'data-msiv-app-promotion-reserved';
  const PRIVACY_UI_SELECTOR = '#shopify-pc__banner, #shopify-pc__prefs__dialog, [id^="shopify-pc__"][role="dialog"]';

  const ACTIVE_OVERLAY_SELECTOR = [
    'newsletter-modal[open]',
    'search-drawer[open]',
    'menu-drawer[open]',
    'cart-drawer[open]',
    'drawer-element[open]',
    'quick-view[open]',
    'x-modal[open]',
    'modal-element[open]',
    'dialog[open]',
    '[role="dialog"][open]',
    '[role="dialog"][aria-modal="true"]:not([aria-hidden="true"])',
    PRIVACY_UI_SELECTOR
  ].join(',');

  function parseJSON(value, fallback) {
    if (value === undefined || value === null || value === '') return fallback;
    try {
      return JSON.parse(value);
    } catch (_error) {
      return fallback;
    }
  }

  function clampNumber(value, minimum, maximum, fallback) {
    const number = Number(value);
    if (!Number.isFinite(number)) return fallback;
    return Math.min(maximum, Math.max(minimum, number));
  }

  function sanitizeToken(value, fallback) {
    const token = String(value || '').trim();
    return /^[a-zA-Z0-9_-]{1,32}$/.test(token) ? token : fallback;
  }

  function sanitizeEventPrefix(value) {
    const prefix = String(value || '').trim();
    return /^[a-zA-Z0-9_-]{1,48}$/.test(prefix) ? prefix : 'msiv_app_promotion';
  }

  function randomToken() {
    try {
      const values = new Uint32Array(4);
      crypto.getRandomValues(values);
      return Array.from(values, (value) => value.toString(16).padStart(8, '0')).join('');
    } catch (_error) {
      return `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`;
    }
  }

  function randomBucket() {
    try {
      const value = new Uint32Array(1);
      crypto.getRandomValues(value);
      return value[0] % 10000;
    } catch (_error) {
      return Math.floor(Math.random() * 10000);
    }
  }

  function isRendered(element) {
    if (!(element instanceof Element) || element.hidden) return false;
    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  }

  function nodeContainsOverlay(node) {
    if (!(node instanceof Element)) return false;
    if (node === document.body) return true;
    try {
      return node.matches(ACTIVE_OVERLAY_SELECTOR) || Boolean(node.querySelector(ACTIVE_OVERLAY_SELECTOR));
    } catch (_error) {
      return false;
    }
  }

  function mutationCanAffectOverlays(mutation) {
    if (nodeContainsOverlay(mutation.target)) return true;
    return Array.from(mutation.addedNodes || []).some(nodeContainsOverlay);
  }

  function detectDevice(navigatorObject = navigator) {
    const userAgent = String(navigatorObject.userAgent || '');
    const clientPlatform = String(navigatorObject.userAgentData?.platform || '');
    const platform = String(navigatorObject.platform || clientPlatform || '');
    const touchPoints = Number(navigatorObject.maxTouchPoints) || 0;

    const android = /^Android$/i.test(clientPlatform) || /Android/i.test(userAgent);
    if (android) {
      const phone = /Mobile/i.test(userAgent) || navigatorObject.userAgentData?.mobile === true;
      return { eligible: true, platform: 'android', deviceType: phone ? 'phone' : 'tablet' };
    }

    if (/iPhone|iPod/i.test(userAgent) || /iPhone|iPod/i.test(platform)) {
      return { eligible: true, platform: 'ios', deviceType: 'phone' };
    }

    const ipad = /iPad/i.test(userAgent) || /iPad/i.test(platform);
    const ipadDesktopMode = touchPoints > 1 && (
      /Macintosh/i.test(userAgent) || /MacIntel|macOS/i.test(platform) || /macOS/i.test(clientPlatform)
    );
    if (ipad || ipadDesktopMode) {
      return { eligible: true, platform: 'ios', deviceType: 'tablet' };
    }

    return { eligible: false, platform: 'unsupported', deviceType: 'unknown' };
  }

  function trafficSource() {
    const params = new URLSearchParams(location.search);
    const source = String(params.get('utm_source') || '').toLowerCase();
    if (/facebook|instagram|meta|fb|ig/.test(source)) return 'meta';
    if (/google|adwords/.test(source)) return 'google';
    if (/email|newsletter|brevo|klaviyo/.test(source)) return 'email';

    if (!document.referrer) return 'direct';
    try {
      const hostname = new URL(document.referrer).hostname.toLowerCase();
      if (/facebook|instagram/.test(hostname)) return 'meta';
      if (/google\./.test(hostname)) return 'google';
      if (hostname === location.hostname) return 'direct';
    } catch (_error) {
      return 'other';
    }
    return 'other';
  }

  function normalizeStoreURL(value, platform) {
    try {
      const url = new URL(String(value || ''), location.origin);
      if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;

      if (platform === 'ios') {
        if (url.hostname !== 'apps.apple.com' || !/\/id6762192244\/?$/.test(url.pathname)) return null;
      } else if (platform === 'android') {
        if (
          url.hostname !== 'play.google.com' ||
          !/^\/store\/apps\/details\/?$/.test(url.pathname) ||
          url.searchParams.get('id') !== 'de.meinschrankistvoll.app'
        ) return null;
      } else {
        return null;
      }
      return url.href;
    } catch (_error) {
      return null;
    }
  }

  class PromotionStorage {
    constructor() {
      this.localAvailable = false;
      this.cookieAvailable = false;
    }

    probe() {
      const probeKey = `msiv:probe:${randomToken().slice(0, 12)}`;

      try {
        localStorage.setItem(probeKey, '1');
        this.localAvailable = localStorage.getItem(probeKey) === '1';
        localStorage.removeItem(probeKey);
      } catch (_error) {
        this.localAvailable = false;
      }

      try {
        this.setCookie(probeKey, '1', 60);
        this.cookieAvailable = this.getCookie(probeKey) === '1';
        this.deleteCookie(probeKey);
      } catch (_error) {
        this.cookieAvailable = false;
      }

      return this.localAvailable || this.cookieAvailable;
    }

    probeReadOnly() {
      try {
        localStorage.getItem(ALREADY_INSTALLED_STORAGE_KEY);
        this.localAvailable = true;
      } catch (_error) {
        this.localAvailable = false;
      }

      try {
        void document.cookie;
        this.cookieAvailable = true;
      } catch (_error) {
        this.cookieAvailable = false;
      }

      return this.localAvailable || this.cookieAvailable;
    }

    getCookie(name) {
      const prefix = `${encodeURIComponent(name)}=`;
      const entry = document.cookie.split('; ').find((item) => item.startsWith(prefix));
      return entry ? decodeURIComponent(entry.slice(prefix.length)) : null;
    }

    setCookie(name, value, maxAgeSeconds) {
      const secure = location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=${Math.max(1, Math.floor(maxAgeSeconds))}; Path=/; SameSite=Lax${secure}`;
    }

    deleteCookie(name) {
      const secure = location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `${encodeURIComponent(name)}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
    }

    parseRecord(raw, now = Date.now()) {
      if (!raw) return null;
      try {
        const record = JSON.parse(raw);
        const shownAt = Number(record.shownAt);
        const nextEligibleAt = Number(record.nextEligibleAt);
        const latestSaneDate = now + (366 * DAY_MS);
        if (record.version !== 1 || !Number.isFinite(shownAt) || !Number.isFinite(nextEligibleAt)) return null;
        if (shownAt < 0 || nextEligibleAt < shownAt || nextEligibleAt > latestSaneDate) return null;
        return { version: 1, shownAt, nextEligibleAt };
      } catch (_error) {
        return null;
      }
    }

    readRecord(now = Date.now()) {
      const records = [];
      if (this.localAvailable) {
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          const record = this.parseRecord(raw, now);
          if (record && record.nextEligibleAt > now) records.push(record);
          else if (raw) localStorage.removeItem(STORAGE_KEY);
        } catch (_error) { }
      }
      if (this.cookieAvailable) {
        try {
          const raw = this.getCookie(COOKIE_KEY);
          const record = this.parseRecord(raw, now);
          if (record && record.nextEligibleAt > now) records.push(record);
          else if (raw) this.deleteCookie(COOKIE_KEY);
        } catch (_error) { }
      }
      return records.sort((a, b) => b.nextEligibleAt - a.nextEligibleAt)[0] || null;
    }

    writeRecord(record, now = Date.now()) {
      const serialized = JSON.stringify(record);
      const maxAge = Math.ceil(Math.max(1, record.nextEligibleAt - now) / 1000);
      let verified = false;

      if (this.localAvailable) {
        try {
          localStorage.setItem(STORAGE_KEY, serialized);
          const saved = this.parseRecord(localStorage.getItem(STORAGE_KEY), now);
          verified = Boolean(saved && saved.nextEligibleAt === record.nextEligibleAt) || verified;
        } catch (_error) { }
      }

      if (this.cookieAvailable) {
        try {
          this.setCookie(COOKIE_KEY, serialized, maxAge);
          const saved = this.parseRecord(this.getCookie(COOKIE_KEY), now);
          verified = Boolean(saved && saved.nextEligibleAt === record.nextEligibleAt) || verified;
        } catch (_error) { }
      }

      return verified;
    }

    parseAlreadyInstalled(raw, now = Date.now()) {
      if (raw === null || raw === undefined || raw === '') return null;
      if (raw === '1') return { installed: true, legacy: true, expiresAt: null };

      if (/^2:\d+$/.test(raw)) {
        const expiresAt = Number(raw.slice(2));
        if (Number.isFinite(expiresAt) && expiresAt > now && expiresAt <= now + ALREADY_INSTALLED_RETENTION_MS) {
          return { installed: true, legacy: false, expiresAt };
        }
        return null;
      }

      try {
        const record = JSON.parse(raw);
        const expiresAt = Number(record?.expiresAt);
        if (
          record?.version === 2 &&
          Number.isFinite(expiresAt) &&
          expiresAt > now &&
          expiresAt <= now + ALREADY_INSTALLED_RETENTION_MS
        ) {
          return { installed: true, legacy: false, expiresAt };
        }
      } catch (_error) { }
      return null;
    }

    readAlreadyInstalled(now = Date.now()) {
      let installed = false;
      let legacy = false;

      if (this.localAvailable) {
        try {
          const value = localStorage.getItem(ALREADY_INSTALLED_STORAGE_KEY);
          const record = this.parseAlreadyInstalled(value, now);
          if (record?.installed) {
            installed = true;
            legacy = record.legacy || legacy;
          } else if (value !== null) {
            localStorage.removeItem(ALREADY_INSTALLED_STORAGE_KEY);
          }
        } catch (_error) { }
      }

      if (this.cookieAvailable) {
        try {
          const value = this.getCookie(ALREADY_INSTALLED_COOKIE_KEY);
          const record = this.parseAlreadyInstalled(value, now);
          if (record?.installed) {
            installed = true;
            legacy = record.legacy || legacy;
          } else if (value !== null) {
            this.deleteCookie(ALREADY_INSTALLED_COOKIE_KEY);
          }
        } catch (_error) { }
      }

      // Older releases stored a bare "1" without an expiry. Keep recognising it,
      // then migrate it best-effort so every new visit is bounded to 180 days.
      if (installed && legacy) this.writeAlreadyInstalled(now);
      return installed;
    }

    writeAlreadyInstalled(now = Date.now()) {
      const expiresAt = now + ALREADY_INSTALLED_RETENTION_MS;
      const localValue = JSON.stringify({ version: 2, selectedAt: now, expiresAt });
      const cookieValue = `2:${expiresAt}`;
      let verified = false;

      if (this.localAvailable) {
        try {
          localStorage.setItem(ALREADY_INSTALLED_STORAGE_KEY, localValue);
          verified = Boolean(this.parseAlreadyInstalled(localStorage.getItem(ALREADY_INSTALLED_STORAGE_KEY), now)) || verified;
        } catch (_error) { }
      }

      if (this.cookieAvailable) {
        try {
          this.setCookie(ALREADY_INSTALLED_COOKIE_KEY, cookieValue, ALREADY_INSTALLED_COOKIE_MAX_AGE);
          verified = Boolean(this.parseAlreadyInstalled(this.getCookie(ALREADY_INSTALLED_COOKIE_KEY), now)) || verified;
        } catch (_error) { }
      }

      return verified;
    }

    readExplicitDismissal(now = Date.now()) {
      // Legacy cookies already have a browser-managed expiry. A legacy session
      // marker has no recoverable creation date: never turn it into a new 10 days.
      let dismissed = false;
      const isCurrent = (value) => {
        if (typeof value !== 'string' || !/^2:\d+$/.test(value)) return false;
        const expiresAt = Number(value.slice(2));
        return Number.isFinite(expiresAt) && expiresAt > now && expiresAt <= now + (90 * DAY_MS);
      };
      try {
        const value = sessionStorage.getItem(DISMISS_SESSION_KEY);
        if (isCurrent(value)) dismissed = true;
        else if (value !== null) sessionStorage.removeItem(DISMISS_SESSION_KEY);
      } catch (_error) { }
      try {
        const value = this.getCookie(DISMISS_COOKIE_KEY);
        if (value === '1' || isCurrent(value)) dismissed = true;
        else if (value !== null) this.deleteCookie(DISMISS_COOKIE_KEY);
      } catch (_error) { }
      return dismissed;
    }

    writeExplicitDismissal(cooldownDays = 10, now = Date.now()) {
      const days = clampNumber(cooldownDays, 1, 90, 10);
      // Closing must not restart a valid cooldown already begun at appearance.
      const record = this.readRecord(now);
      const expiresAt = record && record.nextEligibleAt > now
        ? record.nextEligibleAt
        : now + (days * DAY_MS);
      const value = `2:${expiresAt}`;
      const maxAgeSeconds = Math.ceil((expiresAt - now) / 1000);
      let verified = false;
      try {
        sessionStorage.setItem(DISMISS_SESSION_KEY, value);
        verified = sessionStorage.getItem(DISMISS_SESSION_KEY) === value || verified;
      } catch (_error) { }
      try {
        this.setCookie(DISMISS_COOKIE_KEY, value, maxAgeSeconds);
        verified = this.getCookie(DISMISS_COOKIE_KEY) === value || verified;
      } catch (_error) { }
      return verified;
    }

    readBucket(now = Date.now()) {
      const values = [];
      if (this.localAvailable) {
        try {
          const value = localStorage.getItem(BUCKET_KEY);
          const expiresAt = Number(localStorage.getItem(BUCKET_EXPIRY_KEY));
          if (Number.isFinite(expiresAt) && expiresAt > now && expiresAt <= now + (400 * DAY_MS)) {
            values.push(value);
          } else {
            localStorage.removeItem(BUCKET_KEY);
            localStorage.removeItem(BUCKET_EXPIRY_KEY);
          }
        } catch (_error) { }
      }
      if (this.cookieAvailable) {
        try { values.push(this.getCookie(BUCKET_COOKIE_KEY)); } catch (_error) { }
      }
      for (const value of values) {
        if (typeof value !== 'string' || !/^\d{1,4}$/.test(value)) continue;
        const bucket = Number(value);
        if (Number.isInteger(bucket) && bucket >= 0 && bucket < 10000) return bucket;
      }
      return null;
    }

    getOrCreateBucket(now = Date.now()) {
      const existing = this.readBucket(now);
      if (existing !== null) return existing;

      const bucket = randomBucket();
      const expiresAt = now + (366 * DAY_MS);
      let verified = false;
      if (this.localAvailable) {
        try {
          localStorage.setItem(BUCKET_KEY, String(bucket));
          localStorage.setItem(BUCKET_EXPIRY_KEY, String(expiresAt));
          verified = (
            localStorage.getItem(BUCKET_KEY) === String(bucket) &&
            localStorage.getItem(BUCKET_EXPIRY_KEY) === String(expiresAt)
          ) || verified;
        } catch (_error) { }
      }
      if (this.cookieAvailable) {
        try {
          this.setCookie(BUCKET_COOKIE_KEY, String(bucket), 366 * 24 * 60 * 60);
          verified = this.getCookie(BUCKET_COOKIE_KEY) === String(bucket) || verified;
        } catch (_error) { }
      }
      return verified ? bucket : null;
    }

    clearPromotionalState() {
      try {
        [
          STORAGE_KEY,
          BUCKET_KEY,
          BUCKET_EXPIRY_KEY,
          LEASE_KEY,
          SEARCH_FALLBACK_KEY
        ].forEach((key) => {
          localStorage.removeItem(key);
        });
      } catch (_error) { }

      try { sessionStorage.removeItem(SEARCH_SESSION_KEY); } catch (_error) { }

      [COOKIE_KEY, BUCKET_COOKIE_KEY, SEARCH_SESSION_COOKIE_KEY].forEach((key) => {
        try { this.deleteCookie(key); } catch (_error) { }
      });
    }

    clearAll() {
      this.clearPromotionalState();
    }
  }

  class MsivAppPromotion extends HTMLElement {
    constructor() {
      super();
      this.state = 'new';
      this.startedAt = performance.now();
      this.cleanups = [];
      this.timers = new Map();
      this.abortController = null;
      this.privacyApi = null;
      this.storage = null;
      this.device = null;
      this.dialog = null;
      this.opened = false;
      this.closed = false;
      this.cancelledForPage = false;
      this.overlayInterrupted = false;
      this.hadInteraction = Boolean(navigator.userActivation?.hasBeenActive);
      this.lastInteractionWasKeyboard = false;
      this.eligibilityPromise = null;
      this.scrollSnapshot = null;
      this.focusBeforeOpen = null;
      this.closePromise = null;
      this.closeFinalizer = null;
      this.privacyOverlayObserver = null;
      this.privacyOverlayCheckFrame = null;
      this.slotReserved = false;
      this.slotCommitted = false;
      this.compactElement = null;
      this.compactOpen = false;
      this.compactPersistent = false;
      this.privacyState = 'unavailable';
      this.storageAvailable = false;
      this.suspendedForBFCache = false;
      this.restartTimer = null;
      this.bypassCooldownOnce = false;
      this.compactHandlersBound = false;
      this.compactCopyDefault = '';
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.startedAt = performance.now();
      this.cancelledForPage = false;
      this.overlayInterrupted = false;
      this.opened = false;
      this.closed = false;
      this.closePromise = null;
      this.closeFinalizer = null;
      this.eligibilityPromise = null;
      this.privacyApi = null;
      this.storage = null;
      this.device = null;
      this.focusBeforeOpen = null;
      this.slotReserved = false;
      this.slotCommitted = false;
      this.compactElement = null;
      this.compactOpen = false;
      this.compactPersistent = false;
      this.privacyState = 'unavailable';
      this.storageAvailable = false;
      this.suspendedForBFCache = false;
      this.restartTimer = null;
      this.bypassCooldownOnce = false;
      this.compactHandlersBound = false;
      this.compactCopyDefault = '';
      this.hadInteraction = Boolean(navigator.userActivation?.hasBeenActive);
      this.lastInteractionWasKeyboard = false;
      if (this.dialog) {
        this.dialog.remove();
        this.dialog = null;
      }
      this.hidden = true;
      this.abortController = new AbortController();
      this.config = this.readConfig();
      this.setState('initializing');

      if (window.__msivAppPromotionInstance && window.__msivAppPromotionInstance !== this) {
        this.stop('duplicate_instance');
        return;
      }
      window.__msivAppPromotionInstance = this;

      this.listen(document, 'visitorConsentCollected', () => this.onConsentChanged());
      this.listen(window, 'focus', () => this.onConsentChanged());
      this.listen(document, 'visibilitychange', () => {
        if (document.visibilityState === 'visible') this.onConsentChanged();
      });
      this.listen(document, 'modal:afterShow', (event) => this.onExternalOverlay(event));
      this.listen(document, 'drawer:afterShow', (event) => this.onExternalOverlay(event));
      this.listen(document, 'quickview:open', (event) => this.onExternalOverlay(event));
      this.listen(window, 'pagehide', (event) => this.onPageHide(event));
      this.listen(window, 'pageshow', (event) => this.onPageShow(event));
      this.listen(window, 'storage', (event) => this.onStorageChanged(event));

      ['pointerdown', 'touchstart', 'keydown', 'wheel', 'click'].forEach((eventName) => {
        this.listen(window, eventName, (event) => {
          this.recordTrustedInteraction(event);
        }, { capture: true, passive: eventName !== 'keydown' });
      });

      if (this.config.designMode) {
        this.listen(document, 'shopify:section:select', (event) => {
          if (!event.target?.contains(this)) return;
          this.closed = false;
          this.closePromise = null;
          this.closeFinalizer = null;
          this.cancelledForPage = false;
          this.overlayInterrupted = false;
          if (this.abortController?.signal.aborted) this.abortController = new AbortController();
          this.start();
        });
        this.listen(document, 'shopify:section:deselect', (event) => {
          if (event.target?.contains(this)) this.close('editor_deselect', { immediate: true, publish: false });
        });
      }

      this.start();
    }

    disconnectedCallback() {
      this.cancelledForPage = true;
      this.abortController?.abort();
      this.clearTimers();
      if (this.restartTimer !== null) {
        window.clearTimeout(this.restartTimer);
        this.restartTimer = null;
      }
      if (!this.slotCommitted) this.releasePromotionSlot();
      this.cleanups.splice(0).forEach((cleanup) => {
        try { cleanup(); } catch (_error) { }
      });
      this.destroyPresentation({ restoreFocus: false });
      this.closeFinalizer = null;
      this.eligibilityPromise = null;
      if (window.__msivAppPromotionInstance === this) delete window.__msivAppPromotionInstance;
      this.initialized = false;
    }

    readConfig() {
      const data = this.dataset;
      return {
        enabled: parseJSON(data.enabled, false) === true,
        emergencyStop: parseJSON(data.emergencyStop, true) === true,
        designMode: parseJSON(data.designMode, false) === true,
        testMode: parseJSON(data.testMode, false) === true,
        delayMs: clampNumber(parseJSON(data.delaySeconds, 2), 0, 60, 2) * 1000,
        requireInteraction: parseJSON(data.requireInteraction, false) === true,
        cooldownDays: clampNumber(parseJSON(data.cooldownDays, 10), 1, 90, 10),
        rolloutPercent: clampNumber(parseJSON(data.rolloutPercent, 100), 0, 100, 100),
        variant: sanitizeToken(parseJSON(data.variant, 'v1'), 'v1'),
        iosEnabled: parseJSON(data.iosEnabled, true) === true,
        androidEnabled: parseJSON(data.androidEnabled, true) === true,
        iosURL: parseJSON(data.iosUrl, '') || IOS_STORE_URL,
        androidURL: parseJSON(data.androidUrl, '') || ANDROID_STORE_URL,
        iosLabel: String(parseJSON(data.iosLabel, '') || '').trim() || 'Im App Store laden',
        androidLabel: String(parseJSON(data.androidLabel, '') || '').trim() || 'Bei Google Play laden',
        analyticsEnabled: parseJSON(data.analyticsEnabled, false) === true,
        analyticsPrefix: sanitizeEventPrefix(parseJSON(data.analyticsEvent, 'msiv_app_promotion')),
        pageType: String(parseJSON(data.pageType, 'unknown')),
        sectionId: String(parseJSON(data.sectionId, 'unknown'))
      };
    }

    get isTestMode() {
      return this.config.testMode && this.config.designMode;
    }

    listen(target, type, handler, options = {}) {
      target.addEventListener(type, handler, options);
      this.cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    setState(state) {
      this.state = state;
      this.dataset.msivState = state;
      if (this.dialog) this.dialog.dataset.msivState = state;
      if (this.compactElement) this.compactElement.dataset.msivState = state;
      this.debug(`state: ${state}`);
    }

    debug(message) {
      if (this.config?.designMode) console.info(`[MSIV App Promotion] ${message}`);
    }

    stop(reason, { keepPrivacyWatch = false } = {}) {
      if (!keepPrivacyWatch) this.stopPrivacyOverlayWatch();
      if (this.slotReserved && !this.slotCommitted) this.releasePromotionSlot();
      this.setState(reason);
      return false;
    }

    fail(code, error) {
      // A technical failure must not retry a full-screen advertisement on focus.
      this.cancelledForPage = true;
      this.closed = true;
      this.stopPrivacyOverlayWatch();
      if (this.slotReserved && !this.slotCommitted) this.releasePromotionSlot();
      this.setState(`error:${code}`);
      this.publish('error', { error_code: code });
      if (this.config?.designMode && error) console.warn(`[MSIV App Promotion] ${code}`, error);
      return false;
    }

    isSuppressedAppContext() {
      const params = new URLSearchParams(location.search);
      return window.__MSIV_APP_WEBVIEW__ === true ||
        window.__MSIV_INFO_HIDE_INSTALLED__ === true ||
        params.get('msiv_app') === '1' ||
        /MSIV(?:Fashion)?App\//i.test(navigator.userAgent || '');
    }

    resolveDevice() {
      const override = this.isTestMode ? new URLSearchParams(location.search).get('msiv_platform') : null;
      if (override === 'ios') return { eligible: true, platform: 'ios', deviceType: 'phone' };
      if (override === 'android') return { eligible: true, platform: 'android', deviceType: 'phone' };
      if (this.config.designMode && this.isTestMode) return { eligible: true, platform: 'ios', deviceType: 'phone' };
      return detectDevice();
    }

    async start() {
      if (
        this.eligibilityPromise ||
        this.cancelledForPage ||
        this.opened ||
        this.compactOpen ||
        this.closed
      ) return this.eligibilityPromise;
      let trackedPromise;
      trackedPromise = this.runEligibility()
        .catch((error) => {
          if (this.cancelledForPage || this.abortController?.signal.aborted) return false;
          return this.fail('unexpected', error);
        })
        .finally(() => {
          if (this.eligibilityPromise === trackedPromise) this.eligibilityPromise = null;
        });
      this.eligibilityPromise = trackedPromise;
      return trackedPromise;
    }

    async runEligibility() {
      if (!this.config.enabled || this.config.emergencyStop) return this.stop('disabled');
      if (this.config.designMode && !this.isTestMode) return this.stop('editor_preview_disabled');
      if (this.config.testMode && !this.isTestMode) return this.stop('test_mode_guard');
      if (this.hasAttribute('data-msiv-lite-dismissed')) return this.stop('watchdog_dismissed');
      if (this.isSuppressedAppContext()) return this.stop('app_webview');
      if (!this.isTestMode && this.config.rolloutPercent === 0) return this.stop('rollout_zero');

      this.device = this.resolveDevice();
      if (!this.device.eligible) return this.stop('unsupported_device');
      if (this.device.platform === 'ios' && !this.config.iosEnabled) return this.stop('ios_disabled');
      if (this.device.platform === 'android' && !this.config.androidEnabled) return this.stop('android_disabled');
      const dialogAvailable = typeof HTMLDialogElement !== 'undefined' &&
        typeof HTMLDialogElement.prototype.showModal === 'function';

      if (!(await this.waitForPageReady()) || this.cancelledForPage) return false;

      if (!this.isTestMode) {
        this.storage = new PromotionStorage();
        this.storage.probeReadOnly();
        if (this.storage.readExplicitDismissal()) return this.stop('explicitly_dismissed');
      }

      // Shopify's visible privacy UI always has priority. Keep the promotion
      // resumable instead of cancelling the visit permanently.
      if (!this.isTestMode && this.hasActivePrivacyUI()) {
        this.startPrivacyOverlayWatch();
        return this.stop('waiting_for_privacy_ui', { keepPrivacyWatch: true });
      }

      this.privacyState = this.isTestMode ? 'allowed' : 'unavailable';
      if (!this.isTestMode) {
        try {
          this.privacyApi = await this.loadPrivacyApi();
        } catch (error) {
          if (this.cancelledForPage || this.abortController?.signal.aborted) return false;
          this.privacyApi = null;
          this.debug(`Privacy API unavailable; continuing without persistence (${String(error)})`);
        }

        try {
          this.privacyState = this.readPrivacyState();
        } catch (error) {
          this.privacyState = 'unavailable';
          this.debug(`Privacy state unavailable; continuing without persistence (${String(error)})`);
        }

        if (this.privacyState === 'pending') {
          if (this.hasActivePrivacyUI()) {
            this.startPrivacyOverlayWatch();
            return this.stop('waiting_for_privacy_ui', { keepPrivacyWatch: true });
          }

          this.setState('waiting_for_privacy_grace');
          if (!(await this.wait(PRIVACY_GRACE_MS))) return false;
          if (this.hasActivePrivacyUI()) {
            this.startPrivacyOverlayWatch();
            return this.stop('waiting_for_privacy_ui', { keepPrivacyWatch: true });
          }
          try { this.privacyState = this.readPrivacyState(); } catch (_error) { this.privacyState = 'unavailable'; }
        }

        if (this.privacyState === 'denied') this.purgeStoredState();
      }

      if (!dialogAvailable) return this.stop('dialog_unavailable');

      if (!this.isTestMode) {
        this.storage = this.storage || new PromotionStorage();
        this.storageAvailable = this.privacyState === 'allowed'
          ? this.storage.probe()
          : this.storage.probeReadOnly();

        // "App already installed" is an explicit user choice, independent of
        // analytics/preferences consent. Read it even in compact/ephemeral mode.
        if (this.storageAvailable && this.storage.readAlreadyInstalled()) return this.stop('already_installed');

        // Automatic full-screen promotion requires an allowed, writable cooldown.
        // Otherwise leave the shop available; never infer consent or keep retrying
        // an unremembered promotion on every page.
        if (this.privacyState !== 'allowed' || !this.storageAvailable) {
          return this.stop('persistence_unavailable');
        }

        if (this.privacyState === 'allowed' && this.storageAvailable) {
          const record = this.storage.readRecord();
          if (!this.bypassCooldownOnce && record && Date.now() < record.nextEligibleAt) {
            return this.stop('frequency_blocked');
          }
        }

        if (this.config.rolloutPercent < 100) {
          const storedBucket = this.privacyState === 'allowed' && this.storageAvailable
            ? this.storage.getOrCreateBucket()
            : null;
          const bucket = storedBucket === null ? randomBucket() : storedBucket;
          if (bucket >= Math.round(this.config.rolloutPercent * 100)) return this.stop('rollout_holdout');
        }

        if (this.hasActivePrivacyUI()) {
          this.startPrivacyOverlayWatch();
          return this.stop('waiting_for_privacy_ui', { keepPrivacyWatch: true });
        }
        this.startPrivacyOverlayWatch();
      } else {
        this.storage = new PromotionStorage();
        this.storageAvailable = true;
      }

      if (this.isTestMode || this.privacyState === 'allowed' && this.storageAvailable) {
        this.publish('eligible');
      }
      this.setState('armed');

      if (!this.isTestMode && this.config.requireInteraction && !(await this.waitForInteraction())) {
        return false;
      }
      if (!(await this.waitUntilVisible())) return false;
      if (!(await this.wait(this.isTestMode ? 0 : this.config.delayMs))) return false;

      if (this.cancelledForPage) return false;
      if (!(await this.waitForPresentationSlot())) return false;

      return this.withExclusiveClaim(() => this.presentAndCommit());
    }

    async loadPrivacyApi() {
      if (window.Shopify?.customerPrivacy) return window.Shopify.customerPrivacy;
      if (typeof window.Shopify?.loadFeatures !== 'function') throw new Error('loadFeatures unavailable');

      return new Promise((resolve, reject) => {
        let settled = false;
        const abortSignal = this.abortController?.signal;
        const finish = (callback, value) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          this.timers.delete(timeout);
          abortSignal?.removeEventListener('abort', onAbort);
          callback(value);
        };
        const onAbort = () => finish(reject, new Error('privacy load cancelled'));
        const timeout = window.setTimeout(() => {
          finish(reject, new Error('privacy timeout'));
        }, PRIVACY_TIMEOUT_MS);
        this.timers.set(timeout, onAbort);
        abortSignal?.addEventListener('abort', onAbort, { once: true });

        try {
          window.Shopify.loadFeatures(
            [{ name: 'consent-tracking-api', version: '0.1' }],
            (error) => {
              if (error || !window.Shopify.customerPrivacy) {
                finish(reject, error || new Error('privacy API unavailable'));
              } else {
                finish(resolve, window.Shopify.customerPrivacy);
              }
            }
          );
        } catch (error) {
          finish(reject, error);
        }
      });
    }

    readPrivacyState() {
      if (!this.privacyApi) return 'unavailable';
      const consent = this.privacyApi.currentVisitorConsent();
      const preference = typeof consent?.preferences === 'string'
        ? consent.preferences.trim().toLowerCase()
        : '';
      const preferencesAllowed = this.privacyApi.preferencesProcessingAllowed() === true;

      // Shopify can keep shouldShowBanner() true after the visitor has already
      // accepted or rejected. The explicit visitor decision therefore wins;
      // shouldShowBanner is only a pending signal while no decision exists.
      if (preference === 'yes') return preferencesAllowed ? 'allowed' : 'denied';
      if (preference === 'no') return 'denied';
      if (preference !== '') return 'unavailable';
      if (this.privacyApi.shouldShowBanner() === true) return 'pending';
      return preferencesAllowed ? 'allowed' : 'denied';
    }

    onConsentChanged() {
      // The feature can become available after an initial timeout (notably in
      // delayed in-app browser loads). Adopt it before deciding that this is
      // only a retry signal, so a real visitor decision is never missed.
      if (!this.privacyApi && window.Shopify?.customerPrivacy) {
        this.privacyApi = window.Shopify.customerPrivacy;
      }
      if (!this.privacyApi) {
        if (this.cancelledForPage || this.closed || this.compactOpen) return;
        this.scheduleRestart('privacy_api_retry');
        return;
      }
      let state;
      try { state = this.readPrivacyState(); } catch (_error) { state = 'unavailable'; }
      this.privacyState = state;

      if (state === 'denied' && !this.isTestMode) this.purgeStoredState();

      // Revocation still has to purge promotional storage after this instance
      // has reached a terminal UI state. Only presentation work is terminal.
      if (this.cancelledForPage || this.closed) return;

      if (this.hasActivePrivacyUI()) {
        this.suspendForPrivacyUI();
        return;
      }

      if (this.opened || this.compactOpen) {
        if (state !== 'allowed' && !this.isTestMode) {
          this.close('privacy_unavailable', {
            immediate: true,
            publish: false,
            restoreFocus: !this.hasActivePrivacyUI()
          });
        }
        return;
      }

      this.scheduleRestart(`privacy_${state}`);
    }

    purgeStoredState() {
      const storage = this.storage || new PromotionStorage();
      storage.clearPromotionalState();
    }

    scheduleRestart(reason) {
      if (!this.isConnected || this.closed || this.cancelledForPage || this.restartTimer !== null) return;
      this.debug(`eligibility restart requested: ${reason}`);
      this.abortController?.abort();
      this.clearTimers();

      const attempt = () => {
        this.restartTimer = null;
        if (!this.isConnected || this.closed || this.cancelledForPage) return;
        if (this.eligibilityPromise) {
          this.restartTimer = window.setTimeout(attempt, 50);
          return;
        }

        this.overlayInterrupted = false;
        this.abortController = new AbortController();
        this.closePromise = null;
        this.closeFinalizer = null;
        this.start();
      };

      this.restartTimer = window.setTimeout(attempt, 0);
    }

    suspendPresentation({ restoreFocus = false } = {}) {
      if (this.dialog?.open) {
        try { this.dialog.close(); } catch (_error) { }
      }
      this.unlockScroll();
      this.opened = false;
      if (this.dialog) {
        this.dialog.remove();
        this.dialog = null;
      }
      if (this.compactElement) {
        this.compactElement.hidden = true;
        this.compactElement.setAttribute('aria-hidden', 'true');
        this.compactElement.removeAttribute('data-msiv-lite-visible');
        if (this.compactElement.dataset.msivLiteOwner === 'main') {
          delete this.compactElement.dataset.msivLiteOwner;
        }
        delete this.compactElement.dataset.msivPersistence;
        delete this.compactElement.dataset.msivReason;
      }
      this.removeAttribute('data-msiv-lite-active');
      this.compactOpen = false;
      this.hidden = true;
      this.slotCommitted = false;
      this.releasePromotionSlot({ force: true });
      if (restoreFocus) this.restoreFocus();
    }

    suspendForPrivacyUI() {
      if (this.cancelledForPage || this.closed) return;
      if (this.opened || this.compactOpen) this.bypassCooldownOnce = true;
      this.abortController?.abort();
      this.clearTimers();
      this.startPrivacyOverlayWatch();
      if (this.opened || this.compactOpen) this.suspendPresentation({ restoreFocus: false });
      this.stop('waiting_for_privacy_ui', { keepPrivacyWatch: true });
    }

    onStorageChanged(event) {
      if (
        event.key !== ALREADY_INSTALLED_STORAGE_KEY ||
        this.isTestMode ||
        !this.storage ||
        this.closed
      ) return;

      if (!this.storage.parseAlreadyInstalled(event.newValue)) return;

      this.cancelledForPage = true;
      this.abortController?.abort();
      this.clearTimers();
      this.stopPrivacyOverlayWatch();

      if (this.opened || this.compactOpen) {
        this.close('already_installed_elsewhere', { immediate: true, publish: false });
      } else {
        this.stop('already_installed');
      }
    }

    async waitForPresentationSlot(timeoutMs = CONFLICT_WAIT_MS) {
      const deadline = performance.now() + timeoutMs;
      while (!this.cancelledForPage && !this.closed) {
        if (document.visibilityState !== 'visible') {
          if (!(await this.waitUntilVisible())) return false;
          continue;
        }

        if (this.hasActivePrivacyUI()) {
          this.startPrivacyOverlayWatch();
          this.stop('waiting_for_privacy_ui', { keepPrivacyWatch: true });
          return false;
        }

        const keyboardActive = this.hasActiveTextInput();
        const overlayActive = this.hasActiveOverlay();
        if (!keyboardActive && !overlayActive) {
          this.overlayInterrupted = false;
          return true;
        }

        this.setState(keyboardActive ? 'waiting_for_keyboard' : 'waiting_for_overlay');
        if (performance.now() >= deadline) {
          return this.stop('conflict_timeout', { keepPrivacyWatch: true });
        }
        if (!(await this.wait(CONFLICT_POLL_MS))) return false;
      }
      return false;
    }

    waitForPageReady() {
      if (document.body?.classList.contains('loaded') || document.readyState === 'complete') return Promise.resolve(true);
      return new Promise((resolve) => {
        let completed = false;
        const abortSignal = this.abortController?.signal;
        const cleanup = () => {
          document.removeEventListener('page:loaded', onReady);
          window.removeEventListener('load', onReady);
          abortSignal?.removeEventListener('abort', onAbort);
        };
        const done = (successful) => {
          if (completed) return;
          completed = true;
          cleanup();
          resolve(successful);
        };
        const onReady = () => done(true);
        const onAbort = () => done(false);
        document.addEventListener('page:loaded', onReady, { once: true });
        window.addEventListener('load', onReady, { once: true });
        abortSignal?.addEventListener('abort', onAbort, { once: true });
      });
    }

    waitForInteraction() {
      if (this.hadInteraction || navigator.userActivation?.hasBeenActive) return Promise.resolve(true);
      return new Promise((resolve) => {
        const events = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'click'];
        const abortSignal = this.abortController?.signal;
        let completed = false;
        const cleanup = () => {
          events.forEach((name) => window.removeEventListener(name, done, true));
          abortSignal?.removeEventListener('abort', onAbort);
        };
        const done = (event) => {
          if (!this.recordTrustedInteraction(event)) return;
          if (completed) return;
          completed = true;
          cleanup();
          resolve(true);
        };
        const onAbort = () => {
          if (completed) return;
          completed = true;
          cleanup();
          resolve(false);
        };
        events.forEach((name) => window.addEventListener(name, done, { capture: true, passive: name !== 'keydown' }));
        abortSignal?.addEventListener('abort', onAbort, { once: true });
      });
    }

    recordTrustedInteraction(event) {
      if (event?.isTrusted !== true) return false;
      this.hadInteraction = true;

      if (event.type === 'keydown') {
        this.lastInteractionWasKeyboard = true;
      } else if (event.type === 'click') {
        this.lastInteractionWasKeyboard = event.detail === 0;
      } else if (['pointerdown', 'touchstart', 'wheel'].includes(event.type)) {
        this.lastInteractionWasKeyboard = false;
      }

      if (
        this.state === 'conflict_timeout' &&
        !this.hasActiveOverlay() &&
        !this.hasActiveTextInput()
      ) {
        this.scheduleRestart('conflict_cleared_by_interaction');
      }

      return true;
    }

    waitUntilVisible() {
      if (document.visibilityState === 'visible') return Promise.resolve(true);
      return new Promise((resolve) => {
        const abortSignal = this.abortController?.signal;
        let completed = false;
        const cleanup = () => {
          document.removeEventListener('visibilitychange', onVisibility);
          abortSignal?.removeEventListener('abort', onAbort);
        };
        const finish = (successful) => {
          if (completed) return;
          completed = true;
          cleanup();
          resolve(successful);
        };
        const onVisibility = () => {
          if (document.visibilityState !== 'visible') return;
          finish(true);
        };
        const onAbort = () => finish(false);
        document.addEventListener('visibilitychange', onVisibility);
        abortSignal?.addEventListener('abort', onAbort, { once: true });
      });
    }

    wait(milliseconds) {
      return new Promise((resolve) => {
        let settled = false;
        const finish = (completed) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          this.timers.delete(timer);
          resolve(completed);
        };
        const timer = window.setTimeout(() => {
          finish(true);
        }, Math.max(0, milliseconds));
        this.timers.set(timer, () => finish(false));
      });
    }

    clearTimers() {
      const cancellations = Array.from(this.timers.values());
      this.timers.clear();
      cancellations.forEach((cancel) => cancel());
    }

    hasActiveOverlay() {
      if (document.body.classList.contains('has-modal-open') || document.body.classList.contains('has-modal-opening')) return true;
      if (document.querySelector('newsletter-modal .alert')) return true;
      return Array.from(document.querySelectorAll(ACTIVE_OVERLAY_SELECTOR)).some((element) => {
        return !this.contains(element) && isRendered(element);
      });
    }

    hasActivePrivacyUI() {
      return Array.from(document.querySelectorAll(PRIVACY_UI_SELECTOR)).some(isRendered);
    }

    startPrivacyOverlayWatch() {
      if (this.isTestMode || this.privacyOverlayObserver || !document.body) return;

      const check = () => {
        this.privacyOverlayCheckFrame = null;
        if (this.hasActivePrivacyUI()) {
          this.suspendForPrivacyUI();
          return;
        }
        if (this.state === 'waiting_for_privacy_ui') {
          this.scheduleRestart('privacy_ui_hidden');
          return;
        }
        const overlayActive = this.hasActiveOverlay();
        if (
          !overlayActive &&
          !this.hasActiveTextInput() &&
          ['waiting_for_overlay', 'waiting_for_keyboard', 'conflict_timeout'].includes(this.state)
        ) {
          this.scheduleRestart('presentation_conflict_cleared');
          return;
        }
        if (overlayActive) this.onExternalOverlay({ target: document.body });
      };
      this.privacyOverlayObserver = new MutationObserver((mutations) => {
        if (!mutations.some(mutationCanAffectOverlays)) return;
        if (this.privacyOverlayCheckFrame !== null) return;
        this.privacyOverlayCheckFrame = window.requestAnimationFrame(check);
      });
      this.privacyOverlayObserver.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden']
      });
      this.cleanups.push(() => this.stopPrivacyOverlayWatch());
    }

    stopPrivacyOverlayWatch() {
      this.privacyOverlayObserver?.disconnect();
      this.privacyOverlayObserver = null;
      if (this.privacyOverlayCheckFrame !== null) {
        window.cancelAnimationFrame(this.privacyOverlayCheckFrame);
        this.privacyOverlayCheckFrame = null;
      }
    }

    cancelForPrivacyUI() {
      this.suspendForPrivacyUI();
    }

    hasActiveTextInput() {
      const active = document.activeElement;
      if (!active) return false;
      if (active.matches('input, textarea, select, [contenteditable="true"]')) return true;
      return Boolean(window.visualViewport && window.visualViewport.height < window.innerHeight * 0.72);
    }

    onExternalOverlay(event) {
      if (this.contains(event.target)) return;
      if (this.opened || this.compactOpen) {
        this.overlayInterrupted = true;
        this.bypassCooldownOnce = true;
        this.suspendPresentation({ restoreFocus: false });
        this.setState('waiting_for_overlay');
        this.scheduleRestart('overlay_interrupted');
      } else {
        // Before presentation, drawers/modals/keyboards are transient. The
        // eligibility loop keeps retrying for up to 30 seconds.
        this.setState('waiting_for_overlay');
      }
    }

    async withExclusiveClaim(callback) {
      if (this.isTestMode || this.privacyState !== 'allowed' || !this.storageAvailable) return callback();

      if (navigator.locks?.request) {
        let acquired = false;
        let result = false;
        try {
          await navigator.locks.request(LOCK_NAME, { mode: 'exclusive', ifAvailable: true }, async (lock) => {
            if (!lock) return;
            acquired = true;
            result = await callback();
          });
        } catch (error) {
          if (acquired) return this.fail('web_lock_callback_failed', error);
          this.debug(`Web Locks unavailable at runtime; using local fallback (${String(error)})`);
          return this.withFallbackClaim(callback);
        }
        return acquired ? result : this.stop('other_tab');
      }

      return this.withFallbackClaim(callback);
    }

    async withFallbackClaim(callback) {
      if (!this.storage?.localAvailable || typeof BroadcastChannel !== 'function') {
        this.debug('Cross-tab coordination unavailable; continuing best-effort');
        return callback();
      }

      const token = randomToken();
      const candidates = new Set([token]);
      let otherClaimed = false;
      let channel = null;
      let onMessage = null;
      let callbackStarted = false;

      try {
        channel = new BroadcastChannel(CHANNEL_NAME);
        onMessage = (event) => {
          const message = event.data;
          if (!message || message.token === token) return;
          const otherToken = String(message.token || '');
          if (!/^[a-f0-9]{16,64}$/i.test(otherToken)) return;
          if (message.type === 'candidate') {
            const isNewCandidate = !candidates.has(otherToken);
            candidates.add(otherToken);
            if (isNewCandidate) channel.postMessage({ type: 'candidate', token });
          }
          if (message.type === 'claimed') otherClaimed = true;
        };
        channel.addEventListener('message', onMessage);
        channel.postMessage({ type: 'candidate', token });

        if (!(await this.wait(120 + Math.floor(Math.random() * 80)))) return false;
        if (otherClaimed || Array.from(candidates).sort()[0] !== token) return this.stop('other_tab');

        const now = Date.now();
        let currentLease = null;
        try { currentLease = JSON.parse(localStorage.getItem(LEASE_KEY)); } catch (_error) { }
        if (currentLease && Number(currentLease.expiresAt) > now && currentLease.token !== token) {
          return this.stop('other_tab');
        }

        try {
          localStorage.setItem(LEASE_KEY, JSON.stringify({ token, expiresAt: now + LEASE_MS }));
        } catch (error) {
          this.debug(`Lease storage unavailable; continuing best-effort (${String(error)})`);
          callbackStarted = true;
          return await callback();
        }
        if (!(await this.wait(80))) return false;
        let verified = null;
        try { verified = JSON.parse(localStorage.getItem(LEASE_KEY)); } catch (_error) { }
        if (otherClaimed) return this.stop('other_tab');
        if (!verified || verified.token !== token) {
          this.debug('Lease verification unavailable; continuing best-effort');
          callbackStarted = true;
          return await callback();
        }

        channel.postMessage({ type: 'claimed', token });
        callbackStarted = true;
        return await callback();
      } catch (error) {
        if (callbackStarted) return this.fail('coordination_callback_failed', error);
        this.debug(`Coordination failed; continuing best-effort (${String(error)})`);
        return callback();
      } finally {
        try {
          const lease = JSON.parse(localStorage.getItem(LEASE_KEY));
          if (lease?.token === token) localStorage.removeItem(LEASE_KEY);
        } catch (_error) { }
        if (channel && onMessage) channel.removeEventListener('message', onMessage);
        channel?.close();
      }
    }

    async revalidateBeforeOpen({ compact = false } = {}) {
      if (this.cancelledForPage || document.visibilityState !== 'visible') return false;
      if (this.hasAttribute('data-msiv-lite-dismissed')) {
        this.setState('watchdog_dismissed');
        return false;
      }
      if (this.hasActiveOverlay() || this.hasActiveTextInput()) return false;
      if (this.hasActivePrivacyUI()) return false;

      if (!this.isTestMode) {
        if (this.storage?.readExplicitDismissal()) {
          this.setState('explicitly_dismissed');
          return false;
        }

        let privacyState;
        try { privacyState = this.readPrivacyState(); } catch (_error) { privacyState = 'unavailable'; }
        this.privacyState = privacyState;
        if (privacyState !== 'allowed' || !this.storageAvailable) {
          this.setState('persistence_unavailable');
          return false;
        }
        if (this.storage?.readAlreadyInstalled()) {
          this.setState('already_installed');
          return false;
        }

        if (privacyState === 'allowed' && this.storageAvailable) {
          const record = this.storage?.readRecord();
          if (!this.bypassCooldownOnce && record && Date.now() < record.nextEligibleAt) {
            this.setState('frequency_blocked');
            return false;
          }
        }
      }
      return true;
    }

    stopAfterRevalidationFailure(fallbackReason) {
      const terminalStates = new Set([
        'already_installed',
        'explicitly_dismissed',
        'frequency_blocked',
        'watchdog_dismissed',
        'persistence_unavailable'
      ]);
      return this.stop(terminalStates.has(this.state) ? this.state : fallbackReason);
    }

    reservePromotionSlot() {
      this.slotReserved = true;
      document.documentElement.setAttribute(RESERVED_ATTRIBUTE, 'true');
      document.dispatchEvent(new CustomEvent(RESERVE_EVENT, {
        detail: { sectionId: this.config.sectionId }
      }));
    }

    releasePromotionSlot({ force = false } = {}) {
      if (!this.slotReserved || this.slotCommitted && !force) return;
      this.slotReserved = false;
      document.documentElement.removeAttribute(RESERVED_ATTRIBUTE);
      document.dispatchEvent(new CustomEvent(RELEASE_EVENT, {
        detail: { sectionId: this.config.sectionId }
      }));
    }

    onPageHide(event) {
      this.suspendedForBFCache = event?.persisted === true;
      this.cancelledForPage = true;
      this.abortController?.abort();
      this.clearTimers();
      if (this.restartTimer !== null) {
        window.clearTimeout(this.restartTimer);
        this.restartTimer = null;
      }
      this.suspendPresentation({ restoreFocus: false });
      this.stopPrivacyOverlayWatch();
      if (!this.closed) this.setState(this.suspendedForBFCache ? 'bfcache_suspended' : 'page_hidden');
    }

    onPageShow(event) {
      if (!event.persisted || this.closed) return;

      this.suspendedForBFCache = false;
      this.cancelledForPage = false;
      this.overlayInterrupted = false;
      this.abortController = new AbortController();
      this.closePromise = null;
      this.closeFinalizer = null;

      if (!this.isTestMode && this.storage?.readExplicitDismissal()) {
        this.setState('explicitly_dismissed');
        return;
      }

      if (
        !this.isTestMode &&
        this.privacyState === 'allowed' &&
        this.storageAvailable &&
        this.storage?.readRecord()
      ) {
        this.setState('bfcache_recorded');
        return;
      }

      this.scheduleRestart('bfcache_resume');
    }

    async presentAndCommit() {
      let fullEligible = await this.revalidateBeforeOpen();
      if (!fullEligible && !this.hasActivePrivacyUI() && (this.hasActiveOverlay() || this.hasActiveTextInput())) {
        if (!(await this.waitForPresentationSlot())) return false;
        fullEligible = await this.revalidateBeforeOpen();
      }
      if (!fullEligible) {
        if (this.hasActivePrivacyUI()) {
          this.suspendForPrivacyUI();
          return false;
        }
        return this.stopAfterRevalidationFailure('full_revalidation_failed');
      }

      try {
        this.createDialog();
        this.reservePromotionSlot();
        await this.preparePlatformAssets();
        let assetsEligible = await this.revalidateBeforeOpen();
        if (!assetsEligible && !this.hasActivePrivacyUI() && (this.hasActiveOverlay() || this.hasActiveTextInput())) {
          if (!(await this.waitForPresentationSlot())) {
            this.destroyPresentation({
              restoreFocus: false,
              keepPrivacyWatch: this.state === 'conflict_timeout'
            });
            return false;
          }
          assetsEligible = await this.revalidateBeforeOpen();
        }
        if (!assetsEligible) {
          this.destroyPresentation({ restoreFocus: false });
          if (this.hasActivePrivacyUI()) {
            this.suspendForPrivacyUI();
            return false;
          }
          return this.stopAfterRevalidationFailure('asset_race_fallback');
        }

        this.focusBeforeOpen = document.activeElement;
        this.lockScroll();
        this.setState('opening');
        const initialFocus = this.dialog.querySelector('[data-msiv-close="close_button"]');
        const coarsePointer = window.matchMedia?.('(pointer: coarse)')?.matches === true || navigator.maxTouchPoints > 0;
        if (coarsePointer && this.lastInteractionWasKeyboard !== true) {
          this.dialog.dataset.msivInitialFocus = 'touch';
        } else {
          delete this.dialog.dataset.msivInitialFocus;
        }
        this.dialog.showModal();
        this.opened = true;

        const rectangle = this.dialog.getBoundingClientRect();
        if (
          !this.dialog.open ||
          rectangle.width < 1 ||
          rectangle.height < 1 ||
          !isRendered(this.dialog) ||
          !this.hasExpectedStyles(rectangle)
        ) {
          throw new Error('dialog not presented');
        }

        if (!(await this.nextAnimationFrame())) {
          if (this.state === 'waiting_for_privacy_ui' || this.hasActivePrivacyUI()) {
            this.suspendForPrivacyUI();
            return false;
          }
          this.destroyPresentation({ restoreFocus: false });
          return this.stop('presentation_interrupted');
        }
        if (!this.dialog?.open || !this.opened || this.closed || this.state === 'closing') return false;
        this.dialog.dataset.msivState = 'open';
        if (!(await this.waitForVisiblePresentation())) {
          if (this.state === 'waiting_for_privacy_ui' || this.hasActivePrivacyUI()) {
            this.suspendForPrivacyUI();
            return false;
          }
          this.destroyPresentation({ restoreFocus: false });
          return this.stop('presentation_interrupted');
        }

        const finallyEligible = await this.revalidateBeforeOpen();
        if (
          !this.dialog?.open ||
          !this.opened ||
          this.closed ||
          this.state !== 'opening' ||
          document.visibilityState !== 'visible' ||
          !isRendered(this.dialog) ||
          !this.hasExpectedStyles(this.dialog.getBoundingClientRect()) ||
          !finallyEligible
        ) {
          if (this.hasActivePrivacyUI()) {
            this.suspendForPrivacyUI();
            return false;
          }
          this.destroyPresentation({ restoreFocus: false });
          return this.stopAfterRevalidationFailure('presentation_interrupted');
        }

        if (!this.isTestMode && !this.writeCooldownBestEffort()) {
          this.destroyPresentation({ restoreFocus: true });
          return this.fail('cooldown_write_failed');
        }

        this.slotCommitted = true;
        this.bypassCooldownOnce = false;

        this.setState('open');
        initialFocus?.focus({ preventScroll: true });
        this.publish('shown', {
          time_to_show_ms: Math.round((performance.now() - this.startedAt) / 100) * 100
        });
        return true;
      } catch (error) {
        this.destroyPresentation({ restoreFocus: false });
        // A promotional failure must fail open to the shop, not launch another UI.
        return this.fail('dialog_open_failed', error);
      }
    }

    writeCooldownBestEffort() {
      if (
        this.isTestMode ||
        this.privacyState !== 'allowed' ||
        !this.storageAvailable ||
        !this.storage
      ) return false;

      // Resuming the same interrupted presentation must not extend its cooldown.
      const shownAt = Date.now();
      const existing = this.storage.readRecord(shownAt);
      if (existing && existing.nextEligibleAt > shownAt) return true;
      const record = {
        version: 1,
        shownAt,
        nextEligibleAt: shownAt + (this.config.cooldownDays * DAY_MS)
      };
      const persisted = this.storage.writeRecord(record, shownAt);
      if (!persisted) {
        this.debug('Cooldown write failed; cancelling automatic promotion');
        this.storageAvailable = false;
      }
      return persisted;
    }

    async showCompact(reason) {
      if (this.hasAttribute('data-msiv-lite-dismissed')) return this.stop('watchdog_dismissed');
      this.setState('compact_preparing');
      if (!(await this.waitForPresentationSlot())) return false;
      if (!(await this.revalidateBeforeOpen({ compact: true }))) {
        if (this.hasActivePrivacyUI()) this.suspendForPrivacyUI();
        else this.stop('compact_revalidation_failed');
        return false;
      }

      const lite = this.querySelector('[data-msiv-lite]');
      if (!(lite instanceof HTMLElement)) return this.stop('compact_markup_missing');
      const compactCopy = lite.querySelector('[data-msiv-lite-copy]');
      if (compactCopy && !this.compactCopyDefault) this.compactCopyDefault = compactCopy.textContent || '';
      if (compactCopy) {
        compactCopy.textContent = this.compactCopyDefault;
        compactCopy.removeAttribute('role');
        compactCopy.removeAttribute('aria-live');
      }

      const platformSelector = this.device.platform === 'ios'
        ? '[data-msiv-lite-ios]'
        : '[data-msiv-lite-android]';
      const otherPlatformSelector = this.device.platform === 'ios'
        ? '[data-msiv-lite-android]'
        : '[data-msiv-lite-ios]';
      const platformControl = lite.querySelector(platformSelector);
      const otherPlatformControl = lite.querySelector(otherPlatformSelector);
      const primary = platformControl?.matches('a') ? platformControl : platformControl?.querySelector('a');
      const label = this.device.platform === 'ios' ? this.config.iosLabel : this.config.androidLabel;
      const url = normalizeStoreURL(
        this.device.platform === 'ios' ? this.config.iosURL : this.config.androidURL,
        this.device.platform
      );
      if (!(primary instanceof HTMLAnchorElement) || !url) return this.stop('compact_invalid_store_url');

      if (otherPlatformControl) otherPlatformControl.hidden = true;
      platformControl.hidden = false;
      primary.href = url;
      primary.setAttribute('aria-label', label);

      // The compact UI is only a technical safety net. Never let a rendering
      // failure consume the ten-day full-promotion opportunity or analytics.
      this.compactPersistent = false;
      lite.dataset.msivPersistence = 'ephemeral';
      lite.dataset.msivReason = reason;

      if (!this.compactHandlersBound) {
        this.compactHandlersBound = true;

        lite.querySelectorAll('[data-msiv-lite-close]').forEach((control) => {
          this.listen(control, 'click', (event) => {
            event.preventDefault();
            this.rememberExplicitDismissal();
            this.closeCompact('closed', { action: 'close_button' });
          });
        });

        lite.querySelectorAll('[data-msiv-lite-ios], [data-msiv-lite-android]').forEach((control) => {
          const link = control.matches('a') ? control : control.querySelector('a');
          if (!link) return;
          this.listen(link, 'click', () => {
            if (this.compactPersistent) {
              const eventName = this.device.platform === 'ios' ? 'app_store_click' : 'google_play_click';
              this.publish(eventName, { action: eventName, presentation: 'compact' });
            }
            this.closeCompact('store_navigation', { publish: false });
          });
        });

        const alreadyInstalled = lite.querySelector('[data-msiv-lite-already-installed]');
        if (alreadyInstalled) {
          this.listen(alreadyInstalled, 'click', (event) => {
            event.preventDefault();
            if (alreadyInstalled.disabled) return;
            if (compactCopy) {
              compactCopy.textContent = this.compactCopyDefault;
              compactCopy.removeAttribute('role');
              compactCopy.removeAttribute('aria-live');
            }
            alreadyInstalled.disabled = true;
            const persisted = this.isTestMode || Boolean(this.storage?.writeAlreadyInstalled());
            if (!persisted) {
              alreadyInstalled.disabled = false;
              if (compactCopy) {
                compactCopy.setAttribute('role', 'status');
                compactCopy.setAttribute('aria-live', 'polite');
                compactCopy.textContent = 'Konnte nicht gespeichert werden. Bitte Browser-Einstellungen prüfen.';
              }
              return;
            }
            if (this.compactPersistent && !this.isTestMode) {
              this.publish('already_installed', { action: 'already_installed', presentation: 'compact' });
            }
            this.closeCompact('already_installed', { publish: false });
          });
        }
      }

      this.compactElement = lite;
      lite.dataset.msivLiteOwner = 'main';
      if (!this.isTestMode) this.startPrivacyOverlayWatch();
      this.reservePromotionSlot();
      this.hidden = false;
      this.setAttribute('data-msiv-lite-active', '');
      lite.hidden = false;
      lite.removeAttribute('aria-hidden');
      lite.setAttribute('data-msiv-lite-visible', '');

      if (!(await this.nextAnimationFrame()) || !isRendered(lite)) {
        this.suspendPresentation({ restoreFocus: false });
        return this.stop('compact_not_visible');
      }

      this.compactOpen = true;
      this.slotCommitted = true;
      this.bypassCooldownOnce = false;
      this.setState('compact_open');
      return true;
    }

    closeCompact(reason, options = {}) {
      if (!this.compactElement) return;
      const settings = { publish: true, action: reason, ...options };
      if (settings.publish && this.compactPersistent) {
        this.publish('closed', { action: settings.action, presentation: 'compact' });
      }

      this.compactElement.hidden = true;
      this.compactElement.setAttribute('aria-hidden', 'true');
      this.compactElement.removeAttribute('data-msiv-lite-visible');
      if (this.compactElement.dataset.msivLiteOwner === 'main') {
        delete this.compactElement.dataset.msivLiteOwner;
      }
      delete this.compactElement.dataset.msivReason;
      delete this.compactElement.dataset.msivPersistence;
      this.removeAttribute('data-msiv-lite-active');
      this.compactOpen = false;
      this.hidden = true;
      if (!this.slotCommitted) this.releasePromotionSlot();
      this.closed = true;
      this.stopPrivacyOverlayWatch();
      this.setState('compact_closed');
    }

    createDialog() {
      if (this.dialog) return;
      const template = this.querySelector('template[data-msiv-template]');
      if (!template) throw new Error('template missing');

      const fragment = template.content.cloneNode(true);
      const dialog = fragment.querySelector('[data-msiv-dialog]');
      if (!(dialog instanceof HTMLDialogElement)) throw new Error('dialog missing');

      const primary = dialog.querySelector('[data-msiv-primary]');
      const label = this.device.platform === 'ios' ? this.config.iosLabel : this.config.androidLabel;
      const url = normalizeStoreURL(
        this.device.platform === 'ios' ? this.config.iosURL : this.config.androidURL,
        this.device.platform
      );
      if (!primary || !url) throw new Error('invalid store URL');
      primary.href = url;
      primary.setAttribute('aria-label', label);
      const fallback = primary.querySelector('[data-msiv-primary-label]');
      if (fallback) fallback.textContent = label;

      this.appendChild(fragment);
      this.hidden = false;
      this.dialog = dialog;

      this.listen(this.dialog, 'keydown', (event) => {
        if (event.isTrusted !== true) return;
        this.lastInteractionWasKeyboard = true;
        delete this.dialog?.dataset.msivInitialFocus;
      }, { capture: true });

      this.dialog.querySelectorAll('[data-msiv-close]').forEach((control) => {
        this.listen(control, 'click', (event) => {
          event.preventDefault();
          const action = control.getAttribute('data-msiv-close') || 'close_button';
          this.rememberExplicitDismissal();
          if (action === 'continue_web') this.close('continue_web');
          else this.close('closed', { action });
        });
      });

      const alreadyInstalled = this.dialog.querySelector('[data-msiv-already-installed]');
      if (alreadyInstalled) {
        let alreadyInstalledHandled = false;
        const installedHint = alreadyInstalled.querySelector('.msiv-app-promotion__already-installed-hint');
        const installedHintDefault = installedHint?.textContent || '';
        this.listen(alreadyInstalled, 'click', (event) => {
          event.preventDefault();
          if (alreadyInstalledHandled) return;
          alreadyInstalledHandled = true;
          if (installedHint) {
            installedHint.textContent = installedHintDefault;
            installedHint.removeAttribute('role');
            installedHint.removeAttribute('aria-live');
          }
          alreadyInstalled.disabled = true;

          let persisted = this.isTestMode;
          if (!this.isTestMode) {
            try {
              persisted = Boolean(this.storage?.writeAlreadyInstalled());
            } catch (_error) {
              persisted = false;
            }
          }

          if (!persisted) {
            alreadyInstalledHandled = false;
            alreadyInstalled.disabled = false;
            if (installedHint) {
              installedHint.setAttribute('role', 'status');
              installedHint.setAttribute('aria-live', 'polite');
              installedHint.textContent = 'Konnte nicht gespeichert werden. Bitte Browser-Einstellungen prüfen.';
            }
            if (!this.isTestMode) this.publish('error', { error_code: 'already_installed_write_failed' });
            return;
          }

          if (!this.isTestMode) {
            this.publish('already_installed', { action: 'already_installed' });
          }

          this.close('already_installed', { publish: false });
        });
      }

      this.listen(primary, 'click', () => {
        const eventName = this.device.platform === 'ios' ? 'app_store_click' : 'google_play_click';
        this.publish(eventName, { action: eventName });
        this.close('store_navigation', { immediate: true, publish: false, restoreFocus: false });
      });

      this.listen(this.dialog, 'cancel', (event) => {
        event.preventDefault();
        this.rememberExplicitDismissal();
        this.close('closed', { action: 'escape' });
      });

      this.listen(this.dialog, 'close', (event) => {
        if (event.currentTarget !== this.dialog || this.closed || this.state === 'closing') return;
        if (this.slotCommitted) this.publish('closed', { action: 'native_close' });
        else this.releasePromotionSlot();
        this.destroyPresentation();
        this.closed = true;
        this.setState('closed');
      });
    }

    hasExpectedStyles(rectangle = this.dialog?.getBoundingClientRect()) {
      if (!this.dialog || !rectangle) return false;
      const cssReady = getComputedStyle(this).getPropertyValue('--msiv-css-ready').trim() === '1';
      const rootStyle = getComputedStyle(this.dialog);
      return cssReady &&
        rootStyle.position === 'fixed' &&
        rectangle.width >= window.innerWidth * 0.9 &&
        rectangle.height >= window.innerHeight * 0.9;
    }

    async waitForVisiblePresentation() {
      const deadline = performance.now() + PRESENTATION_TIMEOUT_MS;
      while (
        this.dialog?.open &&
        this.opened &&
        !this.closed &&
        this.state === 'opening' &&
        document.visibilityState === 'visible'
      ) {
        const panel = this.dialog.querySelector('.msiv-app-promotion__panel');
        const opacity = panel ? Number.parseFloat(getComputedStyle(panel).opacity) : 0;
        if (panel && isRendered(panel) && Number.isFinite(opacity) && opacity >= MIN_VISIBLE_OPACITY) return true;
        if (performance.now() >= deadline || !(await this.nextAnimationFrame())) return false;
      }
      return false;
    }

    async preparePlatformAssets() {
      const selectedBadgeSelector = this.device.platform === 'ios' ? '[data-msiv-ios-badge]' : '[data-msiv-android-badge]';
      const otherBadgeSelector = this.device.platform === 'ios' ? '[data-msiv-android-badge]' : '[data-msiv-ios-badge]';
      this.dialog.querySelectorAll(otherBadgeSelector).forEach((image) => image.remove());

      const selectedBadge = this.dialog.querySelector(selectedBadgeSelector);
      if (selectedBadge) selectedBadge.setAttribute('data-msiv-lazy-image', '');
      const images = Array.from(this.dialog.querySelectorAll('img[data-msiv-lazy-image]'));

      await Promise.all(images.map(async (image) => {
        const source = parseJSON(image.getAttribute('data-src'), '');
        const sourceSet = parseJSON(image.getAttribute('data-srcset'), '');
        const sizes = image.getAttribute('data-sizes') || '';
        if (!source) {
          image.remove();
          return;
        }

        if (sourceSet) image.srcset = sourceSet;
        if (sizes) image.sizes = sizes;
        image.src = source;

        try {
          await Promise.race([
            typeof image.decode === 'function' ? image.decode() : this.waitForImage(image),
            this.wait(IMAGE_TIMEOUT_MS).then((completed) => {
              throw new Error(completed ? 'image timeout' : 'image load cancelled');
            })
          ]);
        } catch (_error) {
          if (!image.complete || image.naturalWidth < 1) image.remove();
        }
      }));

      const primary = this.dialog.querySelector('[data-msiv-primary]');
      const remainingBadge = primary?.querySelector('.msiv-app-promotion__store-badge');
      const fallback = primary?.querySelector('[data-msiv-primary-label]');
      if (remainingBadge && fallback) fallback.hidden = true;
    }

    waitForImage(image) {
      if (image.complete) return image.naturalWidth > 0 ? Promise.resolve() : Promise.reject(new Error('image failed'));
      return new Promise((resolve, reject) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', reject, { once: true });
      });
    }

    nextAnimationFrame() {
      return new Promise((resolve) => {
        const abortSignal = this.abortController?.signal;
        if (abortSignal?.aborted) {
          resolve(false);
          return;
        }

        let settled = false;
        const finish = (completed) => {
          if (settled) return;
          settled = true;
          window.cancelAnimationFrame(frame);
          abortSignal?.removeEventListener('abort', onAbort);
          resolve(completed);
        };
        const onAbort = () => finish(false);
        const frame = window.requestAnimationFrame(() => finish(true));
        abortSignal?.addEventListener('abort', onAbort, { once: true });
      });
    }

    lockScroll() {
      if (this.scrollSnapshot) return;
      const bodyStyle = document.body.style;
      this.scrollSnapshot = {
        x: window.scrollX,
        y: window.scrollY,
        position: bodyStyle.position,
        top: bodyStyle.top,
        left: bodyStyle.left,
        right: bodyStyle.right,
        width: bodyStyle.width,
        overflow: bodyStyle.overflow
      };
      document.body.classList.add('msiv-app-promotion-open');
      bodyStyle.position = 'fixed';
      bodyStyle.top = `-${this.scrollSnapshot.y}px`;
      bodyStyle.left = `-${this.scrollSnapshot.x}px`;
      bodyStyle.right = '0';
      bodyStyle.width = '100%';
      bodyStyle.overflow = 'hidden';
    }

    unlockScroll() {
      if (!this.scrollSnapshot) return;
      const snapshot = this.scrollSnapshot;
      const bodyStyle = document.body.style;
      document.body.classList.remove('msiv-app-promotion-open');
      bodyStyle.position = snapshot.position;
      bodyStyle.top = snapshot.top;
      bodyStyle.left = snapshot.left;
      bodyStyle.right = snapshot.right;
      bodyStyle.width = snapshot.width;
      bodyStyle.overflow = snapshot.overflow;
      this.scrollSnapshot = null;
      window.scrollTo(snapshot.x, snapshot.y);
    }

    rememberExplicitDismissal() {
      if (this.isTestMode) return true;
      const storage = this.storage || new PromotionStorage();
      this.storage = storage;
      const persisted = storage.writeExplicitDismissal(this.config.cooldownDays);
      if (!persisted) this.debug('Explicit dismissal could not be persisted');
      return persisted;
    }

    close(reason, options = {}) {
      if (this.compactOpen && !this.dialog) return this.closeCompact(reason, options);
      if (!this.dialog) return this.closePromise;
      if (this.closePromise) {
        if (options.immediate) this.closeFinalizer?.();
        return this.closePromise;
      }
      const settings = {
        immediate: false,
        publish: true,
        restoreFocus: true,
        action: reason,
        ...options
      };

      this.setState('closing');
      if (settings.publish && reason === 'continue_web') {
        this.publish('continue_web', { action: 'continue_web' });
      } else if (settings.publish && reason === 'closed') {
        this.publish('closed', { action: settings.action || 'close' });
      } else if (settings.publish && reason === 'overlay_conflict') {
        this.publish('closed', { action: settings.action || 'overlay_conflict' });
      }

      const duration = settings.immediate || matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 280;
      let finalized = false;
      const finalize = () => {
        if (finalized) return;
        finalized = true;
        this.closeFinalizer = null;
        if (!this.slotCommitted) this.releasePromotionSlot();
        if (this.dialog?.open) this.dialog.close();
        this.destroyPresentation({ restoreFocus: settings.restoreFocus });
        this.closed = true;
        this.setState('closed');
      };
      this.closeFinalizer = finalize;

      if (duration === 0) {
        finalize();
        this.closePromise = Promise.resolve();
        return this.closePromise;
      }

      this.closePromise = this.wait(duration).then((completed) => {
        if (completed) finalize();
      });
      return this.closePromise;
    }

    destroyPresentation({ restoreFocus = true, keepPrivacyWatch = false } = {}) {
      if (this.dialog?.open) {
        try { this.dialog.close(); } catch (_error) { }
      }
      this.unlockScroll();
      this.opened = false;
      if (!keepPrivacyWatch) this.stopPrivacyOverlayWatch();

      if (this.dialog) {
        this.dialog.remove();
        this.dialog = null;
      }
      if (this.compactElement) {
        this.compactElement.hidden = true;
        this.compactElement.setAttribute('aria-hidden', 'true');
        this.compactElement.removeAttribute('data-msiv-lite-visible');
        if (this.compactElement.dataset.msivLiteOwner === 'main') {
          delete this.compactElement.dataset.msivLiteOwner;
        }
        delete this.compactElement.dataset.msivReason;
        delete this.compactElement.dataset.msivPersistence;
      }
      this.removeAttribute('data-msiv-lite-active');
      this.compactOpen = false;
      this.hidden = true;

      if (restoreFocus) this.restoreFocus();
    }

    restoreFocus() {
      const target = this.focusBeforeOpen;
      this.focusBeforeOpen = null;
      if (target instanceof HTMLElement && target.isConnected && isRendered(target)) {
        target.focus({ preventScroll: true });
        if (document.activeElement === target) return;
      }

      const main = document.getElementById('MainContent');
      if (!main) return;
      const hadTabindex = main.hasAttribute('tabindex');
      const previousTabindex = main.getAttribute('tabindex');
      main.setAttribute('tabindex', '-1');
      main.focus({ preventScroll: true });
      if (hadTabindex) main.setAttribute('tabindex', previousTabindex);
      else main.removeAttribute('tabindex');
    }

    publish(suffix, extra = {}) {
      if (!this.config.analyticsEnabled || this.isTestMode || !this.privacyApi) return;
      try {
        if (!this.storageAvailable || this.readPrivacyState() !== 'allowed') return;
        if (this.privacyApi.analyticsProcessingAllowed() !== true) return;
        if (typeof window.Shopify?.analytics?.publish !== 'function') return;

        const allowedPayload = {
          platform: this.device?.platform || 'unknown',
          device_type: this.device?.deviceType || 'unknown',
          page_type: this.config.pageType,
          variant: this.config.variant,
          rollout_group: 'exposed',
          traffic_source: trafficSource(),
          ...extra
        };
        const publication = window.Shopify.analytics.publish(`${this.config.analyticsPrefix}:${suffix}`, allowedPayload);
        if (publication && typeof publication.then === 'function') {
          Promise.resolve(publication).catch(() => { });
        }
      } catch (_error) { }
    }
  }

  customElements.define(ELEMENT_NAME, MsivAppPromotion);

  if (window.__MSIV_APP_PROMOTION_TEST__ === true) {
    window.__MSIV_APP_PROMOTION_INTERNALS__ = {
      PromotionStorage,
      detectDevice,
      normalizeStoreURL,
      parseJSON
    };
  }
})();
